import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from './layout/TopBar';
import BottomNav from './BottomNav';
import Phone from './Phone';
import { fmtCurrency, useDrillAuth } from './drillHelpers';
import { TipsShimmer } from './Shimmer';
import { useDesktopTopBar } from '../context/DesktopLayoutContext';
import { useIsDesktop } from './reports/reportTokens';
import { todayStr, yesterdayStr, getStartEndFromFilter, isTodayRange } from '../services/reportsApi';
import { useStoreTimeZone } from '../utils/dateTimeUtils';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchLiveByChannelRequest, fetchLiveStaffPerformanceRequest, fetchSnapshotDetailsRequest } from '../redux/reports/clickHouse/clickHouseActions';
import type { LiveByChannelItem, LiveStaffPerformanceItem, SnapshotDetailsResponse } from '../interface/clickHouseInterface';
import { TipsGratuityDrill, buildTipsGratuityData, TG_DRILL_TITLE } from './reports/components/TipsGratuity';
import { useRefundLogPeriod } from './reports/refunds/useRefundLogPeriod';
import { tipRefundsByCheckOwner } from './reports/refunds/refundModel';

/**
 * ZAR-1762 / TG-2 — mobile "Tips & Gratuity" drill-in.
 *
 * Renders the same shared two-section drill the desktop Snapshot uses, off the same
 * ClickHouse feeds (staff-performance + by-channel), so the two never diverge.
 */
const DrillTips = ({ onBack, initialDateFilter, initialDate, snapshotValues: snapshotValuesProp, orderSource }: { onBack?: () => void; initialDateFilter?: string; initialDate?: string; snapshotValues?: any; orderSource?: 'direct' | 'thirdparty' | null } = {} as any) => {
    const isEmbedded = !!onBack;
    const navigate = useNavigate();
    const isDesktop = useIsDesktop();

    useEffect(() => {
        if (!isEmbedded && isDesktop) navigate('/reports/snapshot?drill=tips', { replace: true });
    }, [isDesktop, navigate, isEmbedded]);
    const setTopBarProps = useDesktopTopBar();
    const { locationId, selectedDate: drillDate, dateFilter: drillDateFilter, snapshotValues: snapshotValuesAuth } = useDrillAuth();
    // Embedded (mobile SnapshotScreen) passes snapshotValues via prop; standalone reads it from navigation state.
    const snapshotValues = snapshotValuesProp ?? snapshotValuesAuth;
    const storeTz = useStoreTimeZone();
    const today = useMemo(() => todayStr(storeTz), [storeTz]);
    const yesterday = useMemo(() => yesterdayStr(storeTz), [storeTz]);
    const [selectedDate, setSelectedDate] = useState(initialDate ?? drillDate ?? today);
    const [activeDateFilter, setActiveDateFilter] = useState(initialDateFilter ?? drillDateFilter ?? 'Today');
    const dateRange = useMemo(() => {
        const range = getStartEndFromFilter(activeDateFilter, today, yesterday);
        return range || { startDate: selectedDate, endDate: selectedDate };
    }, [activeDateFilter, selectedDate, today, yesterday]);

    // Resolved-range check so the date picker's today matches the Today pill (ZAR-1762).
    const isToday = isTodayRange(dateRange.startDate, dateRange.endDate, today);
    // ClickHouse date params — omitted for Today (live path), passed only for historical ranges
    const chDates: { startDate?: string; endDate?: string } = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
    const orderSourceParam: { orderSource?: 'direct' | 'thirdparty' } = orderSource ? { orderSource } : {};

    /*
     * REF-6 — the period's refund records, so the Staff Tips ranking can net tip/gratuity
     * refunds and show the tip-out exposure icon, same as the desktop drill. Real data off
     * GET /sales/live/v2/refund-log.
     */
    const { summary: refundSummary } = useRefundLogPeriod({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        source: orderSource === 'direct' ? 'direct' : orderSource === 'thirdparty' ? 'thirdparty' : 'overall',
    });

    const dispatch = useAppDispatch();
    const liveStaffPerformance: LiveStaffPerformanceItem[] = useAppSelector((s) => s.clickHouse.liveStaffPerformanceSuccess);
    const liveStaffPerformanceLoading: boolean = useAppSelector((s) => s.clickHouse.liveStaffPerformanceLoading);
    const liveStaffPerformanceFailure: boolean = useAppSelector((s) => s.clickHouse.liveStaffPerformanceFailure);
    const liveByChannel: LiveByChannelItem[] = useAppSelector((s) => s.clickHouse.liveByChannelSuccess);
    const liveByChannelLoading: boolean = useAppSelector((s) => s.clickHouse.liveByChannelLoading);
    const liveByChannelFailure: boolean = useAppSelector((s) => s.clickHouse.liveByChannelFailure);
    // Section totals, order counts and Net Sales all come from snapshot-details (ZAR-1762), the
    // same source the Tips & Grat card reads — not from the snapshotValues nav payload, which
    // predates these keys.
    const snapshotDetails: SnapshotDetailsResponse | null = useAppSelector((s) => s.clickHouse.snapshotDetailsSuccess);
    const snapshotDetailsLoading: boolean = useAppSelector((s) => s.clickHouse.snapshotDetailsLoading);

    const [loading, setLoading] = useState(!isToday);

    const handleDateFilterChange = useCallback((opt: string) => {
        setActiveDateFilter(opt);
        if (opt === 'Today') setSelectedDate(today);
        else if (opt === 'Yesterday') setSelectedDate(yesterday);
    }, [today, yesterday]);

    useEffect(() => {
        if (isEmbedded) return;
        setTopBarProps({
            title: TG_DRILL_TITLE,
            onBack: () => navigate('/reports/snapshot'),
            dateFilters: { value: activeDateFilter, onChange: handleDateFilterChange },
        });
    }, [setTopBarProps, activeDateFilter, handleDateFilterChange, navigate, isEmbedded]);

    // Standalone dispatch (embedded: parent SnapshotScreen dispatches). Always ClickHouse,
    // passing startDate/endDate so historical date filters work too.
    useEffect(() => {
        if (!locationId) {
            setLoading(false);
            return;
        }
        if (!isEmbedded) {
            dispatch(fetchSnapshotDetailsRequest(locationId, chDates.startDate, chDates.endDate, orderSource ?? undefined));
            dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDates, ...orderSourceParam }));
            dispatch(fetchLiveByChannelRequest({ locationId, ...chDates, ...orderSourceParam }));
        }
        setLoading(false);
    }, [locationId, isEmbedded, dateRange.startDate, dateRange.endDate, orderSource, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

    const num = (v: any) => Number(v ?? 0) || 0;
    // Tip Rate / Gratuity Rate use the screen's Net Sales, matching the desktop drill.
    // snapshotValues is the fallback for a standalone open that beat the fetch.
    const netSales = num(snapshotDetails?.netSales ?? snapshotValues?.netSales);

    const drillData = useMemo(() => buildTipsGratuityData({
        staff: liveStaffPerformance.map((s) => ({
            name: s.staff_name,
            tip: s.tip ?? 0,
            gratuity: s.gratuity ?? 0,
            orderCount: s.orderCount ?? 0,
            netSales: s.netSales ?? 0,
        })),
        channels: liveByChannel.map((c) => ({ channel: c.channel, tip: c.tip ?? 0, gratuity: c.gratuity ?? 0 })),
        netSales,
        totalOrders: num(snapshotDetails?.orderCount),
        tipsTotal: num(snapshotDetails?.tip),
        gratuityTotal: num(snapshotDetails?.gratuity),
        ordersWithTips: num(snapshotDetails?.ordersWithTips),
        ordersWithGratuity: num(snapshotDetails?.ordersWithGratuity),
        // REF-6 — nets the Staff Tips ranking and carries the tip-out exposure icon.
        tipRefundsByCheckOwner: tipRefundsByCheckOwner(refundSummary.records),
    }), [liveStaffPerformance, liveByChannel, netSales, snapshotDetails, refundSummary]); // eslint-disable-line react-hooks/exhaustive-deps

    const isDataLoading = snapshotDetailsLoading || liveStaffPerformanceLoading || loading;

    const inner = isDataLoading ? <TipsShimmer /> : (
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px' }}>
            <TipsGratuityDrill
                data={drillData}
                fmt={fmtCurrency}
                isMobile
                staffFailure={liveStaffPerformanceFailure}
                onRetryStaff={() => dispatch(fetchLiveStaffPerformanceRequest({ locationId, ...chDates, ...orderSourceParam }))}
                channelLoading={liveByChannelLoading}
                channelFailure={liveByChannelFailure}
                onRetryChannel={() => dispatch(fetchLiveByChannelRequest({ locationId, ...chDates, ...orderSourceParam }))}
            />
        </div>
    );

    if (isEmbedded) return <>{inner}</>;
    return (
        <Phone>
            <TopBar variant="mobile" />
            {inner}
            <BottomNav />
        </Phone>
    );
};

export default DrillTips;
